/**
 * Writing a timesheet.
 *
 * The entry form used to take the project as free text, which quietly disabled
 * most of the module: the server resolves billability and rates from
 * `projectRef`, not from the label, so a typed name meant every activity
 * defaulted to billable, per-project rates could never match, and the reporting
 * screens grouped "Depot retrofit" and "depot retrofit " as two projects.
 *
 * These are the rules for turning what the form holds into what the API takes,
 * kept out of the component so the validation can be tested without rendering.
 */

/** A project as the picker offers it. `billable` decides the row's default. */
export const projectOptions = (projects = []) =>
  (projects || [])
    .filter((p) => p && p._id && !p.isDeleted)
    .map((p) => ({
      value: String(p._id),
      label: p.code ? `${p.name} · ${p.code}` : p.name,
      name: p.name,
      billable: p.billable !== false,
    }));

export const findProject = (options = [], id) =>
  options.find((o) => o.value === String(id)) || null;

/**
 * One activity, in the shape the API validates.
 *
 * Both `projectRef` and `project` are sent: the ref is what the server computes
 * from, and the label is a denormalised snapshot so a renamed or deleted
 * project does not blank out last quarter's timesheets.
 *
 * `billable` is deliberately NOT sent. The server already resolves it from the
 * project (`projBillable && a.billable !== false`), so sending our own value
 * would be a second opinion on a question the project already answers — and
 * the only thing it could do is silently override the project's setting.
 */
export const toActivityPayload = (activity, options = []) => {
  const picked = findProject(options, activity?.projectRef);
  const payload = {
    date: activity?.date,
    taskName: activity?.taskName || "",
    hours: Number(activity?.hours),
  };
  if (picked) {
    payload.projectRef = picked.value;
    payload.project = picked.name;
  } else if (activity?.project) {
    // Free-text fallback, used only where no projects are configured.
    payload.project = activity.project;
  }
  return payload;
};

/** A row the user has not started. Blank rows are dropped, not complained about. */
export const isBlankRow = (a) =>
  !a?.date && !a?.projectRef && !a?.project && !a?.taskName && !String(a?.hours || "").trim();

/**
 * What is wrong with one row, or null.
 *
 * Returns the first problem rather than a list: the row is one line on screen
 * and has room for one message.
 */
export const rowError = (a, { requireProject = false, maxHoursPerDay = 0, hasProjects = false } = {}) => {
  if (isBlankRow(a)) return null;
  if (!a?.date) return "Pick a date.";
  const hours = Number(a?.hours);
  if (!a?.hours || Number.isNaN(hours)) return "Enter hours.";
  if (hours <= 0) return "Hours must be more than zero.";
  if (maxHoursPerDay > 0 && hours > maxHoursPerDay) {
    return `More than the ${maxHoursPerDay}h daily limit.`;
  }
  // Only insist on a project where there are projects to pick.
  if (requireProject && hasProjects && !a?.projectRef) return "Pick a project.";
  return null;
};

/** Rows the user actually filled in. */
export const filledRows = (activities = []) => activities.filter((a) => !isBlankRow(a));

/**
 * Hours per calendar day across the whole sheet.
 *
 * The daily cap is a property of the day, not of one row: three rows of four
 * hours on the same date is a twelve-hour day, and checking each row alone
 * would let it through. The server enforces the same rule, so catching it here
 * is about telling the person before they submit rather than about trust.
 */
export const hoursByDay = (activities = []) => {
  const byDay = new Map();
  filledRows(activities).forEach((a) => {
    const day = String(a.date || "").slice(0, 10);
    if (!day) return;
    byDay.set(day, (byDay.get(day) || 0) + (Number(a.hours) || 0));
  });
  return byDay;
};

export const dayOverCap = (activities = [], maxHoursPerDay = 0) => {
  if (!maxHoursPerDay || maxHoursPerDay <= 0) return null;
  for (const [day, total] of hoursByDay(activities)) {
    if (total > maxHoursPerDay) return { day, total, cap: maxHoursPerDay };
  }
  return null;
};

/**
 * Whether the sheet can be sent, and why not.
 *
 * The period is checked by the form itself, beside the two date fields it is
 * about; this covers the activity table.
 */
export const entryError = (activities = [], settings = {}, hasProjects = false) => {
  const rows = filledRows(activities);
  if (!rows.length) return "Add at least one activity.";

  const opts = {
    requireProject: Boolean(settings?.requireProject),
    maxHoursPerDay: Number(settings?.maxHoursPerDay) || 0,
    hasProjects,
  };
  for (const row of rows) {
    const err = rowError(row, opts);
    if (err) return err;
  }
  const over = dayOverCap(rows, opts.maxHoursPerDay);
  if (over) {
    return `${over.day} totals ${over.total}h across its rows, over the ${over.cap}h daily limit.`;
  }
  return null;
};
